/**
 * Normalizes text for combobox searching: lowercases it and collapses every
 * run of non-alphanumeric characters into a single space.
 *
 * This is what lets a user type `#47` and match `RO #47`, or type
 * `emett layhue` and match `Emett Layhue (2019 Honda Civic)` — hashes, em
 * dashes and parentheses stop being significant on either side. `\p{L}` and
 * `\p{N}` (rather than `a-z0-9`) keep accented letters intact so an accented
 * customer name is still searchable by its own spelling.
 */
export function normalizeComboboxText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/**
 * True when every whitespace-separated token in `search` appears somewhere in
 * `haystack` (token-AND), after both sides are normalized.
 *
 * Deliberately a boolean rather than a graded relevance score: the caller
 * converts it to cmdk's 1/0, which leaves cmdk rendering options in the order
 * they were supplied instead of re-ranking them by fuzzy score.
 */
export function comboboxMatches(haystack: string, search: string): boolean {
  const tokens = normalizeComboboxText(search).split(" ").filter(Boolean);
  if (tokens.length === 0) {
    return true;
  }
  const normalizedHaystack = normalizeComboboxText(haystack);
  return tokens.every((token) => normalizedHaystack.includes(token));
}

/**
 * Per-search-token match quality against a single haystack token: 3 for an
 * exact match, 2 for a prefix match, 1 for a substring match, 0 for no match.
 */
function tokenLevel(haystackToken: string, searchToken: string): number {
  if (haystackToken === searchToken) {
    return 3;
  }
  if (haystackToken.startsWith(searchToken)) {
    return 2;
  }
  if (haystackToken.includes(searchToken)) {
    return 1;
  }
  return 0;
}

/**
 * Graded relevance score for ranking, not just filtering. `comboboxMatches`
 * stays the readable boolean predicate for pure token-AND filtering (and is
 * directly unit-tested); this is layered on top of the same semantics so a
 * caller that wants match *quality* — not just presence — can rank results
 * without changing what counts as a match.
 *
 * Every search token must match some haystack token (same token-AND
 * semantics as `comboboxMatches` — `comboboxScore(h, s) > 0` always agrees
 * with `comboboxMatches(h, s)`), or the score is 0. For each search token,
 * take the *best* level it achieves against any haystack token (3 exact / 2
 * prefix / 1 substring), then take the *minimum* of those per-token levels
 * across all search tokens — the weakest-matching token caps the score, so
 * an all-exact match outranks one where only some tokens are exact.
 *
 * An empty/punctuation-only search matches everything uniformly (mirrors
 * `comboboxMatches`'s empty-search behavior) so it can never reorder a list.
 */
export function comboboxScore(haystack: string, search: string): number {
  const searchTokens = normalizeComboboxText(search).split(" ").filter(Boolean);
  if (searchTokens.length === 0) {
    return 1;
  }
  const haystackTokens = normalizeComboboxText(haystack)
    .split(" ")
    .filter(Boolean);

  let minLevel = Infinity;
  for (const searchToken of searchTokens) {
    let bestLevel = 0;
    for (const haystackToken of haystackTokens) {
      const level = tokenLevel(haystackToken, searchToken);
      if (level > bestLevel) {
        bestLevel = level;
      }
    }
    if (bestLevel === 0) {
      return 0;
    }
    if (bestLevel < minLevel) {
      minLevel = bestLevel;
    }
  }
  return minLevel;
}
